import { QuestionDialogRaw } from "@/controls/modals/QuestionDialogRaw";
import { invalidateWorkOrderQueries } from "@/pages/workOrders/hooks/invalidateWorkOrderQueries";
import {
	type CalendarEvent,
	deleteCalendarEvent,
} from "@/shared/repositories/CalendarEventRepository";
import type { PlanChangeRef } from "@/shared/repositories/PlanChangeRepository";
import { deleteWorkOrder } from "@/shared/repositories/WorkOrderRepository";
import { invalidatePlanQueries } from "@/shared/utils/planCache";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

export type DeleteTarget = Pick<
	CalendarEvent,
	"EventId" | "EventTypeCode" | "AssetId" | "WorkOrderId" | "WorkOrderNumber"
>;

export const useDeleteCalendarItem = (
	onDeleted: (refs: PlanChangeRef[]) => void,
	onError: (message: string) => void,
) => {
	const queryClient = useQueryClient();
	// kept after close: no text flip during fade-out
	const [target, setTarget] = useState<DeleteTarget | null>(null);
	const [open, setOpen] = useState(false);

	const mutation = useMutation({
		mutationFn: (t: DeleteTarget) =>
			t.WorkOrderId != null
				? deleteWorkOrder(t.WorkOrderId)
				: deleteCalendarEvent(t.EventId as number),
		onSuccess: async (refs, t) => {
			if (t.WorkOrderId != null) await invalidateWorkOrderQueries(queryClient);
			else
				await invalidatePlanQueries(queryClient, {
					assetIds:
						t.EventTypeCode === "H"
							? undefined
							: [t.AssetId, ...refs.map((r) => r.assetId)],
					workOrders: true,
					planChange: true,
				});
			// A delete is an undo batch like any other plan change
			onDeleted(refs);
		},
		onError: (e: Error) => onError(e.message),
	});

	const isWo = target?.WorkOrderId != null;
	const dialogs = (
		<QuestionDialogRaw
			title={isWo ? "Delete work order" : "Delete event"}
			message={
				isWo
					? `Work order ${target?.WorkOrderNumber ?? "—"} will be deleted. Are you sure?`
					: "This event will be removed from the calendar. Are you sure?"
			}
			open={open}
			onClose={(answer) => {
				setOpen(false);
				if (answer === "YES" && target) mutation.mutate(target);
			}}
		/>
	);

	const requestDelete = (t: DeleteTarget) => {
		setTarget(t);
		setOpen(true);
	};

	return { requestDelete, deleting: mutation.isLoading, dialogs };
};
